/**
 * 开通弹窗内容渲染器注册表（通用，对具体业务零认知）
 *
 * store 只放可序列化的 { variant, data }；业务功能用 registerPaymentContent
 * 注册 (data) => 插槽JSX；弹窗用 resolvePaymentContent 按 variant 查表渲染。
 * 查不到 / 渲染器抛错 → 返回空插槽，由弹窗兜底现状文案。
 */
import type { ReactNode } from 'react'
import type { PaymentOrderObserverFactory } from './hooks/paymentOrderObserver.types'

export type PaymentTabKey = 'basic' | 'advanced' | 'super' | 'enterprise'

/** 渲染器产出的"插槽内容" · 缺哪个弹窗兜底哪个 */
export interface PaymentContentSlots {
  /** 可选完整支付界面；由业务提供，缺省继续使用公共弹窗。 */
  modal?: ReactNode
  title?: ReactNode
  subtitle?: ReactNode
  /** 当前选中档位对应的标题与说明；未提供时沿用通用标题。 */
  tabHeaders?: Partial<
    Record<PaymentTabKey, { title: ReactNode; subtitle: ReactNode }>
  >
  /** 业务内容按会员档位为支付弹窗头部提供的视觉主题类名。 */
  headerClassNames?: Partial<Record<PaymentTabKey, string>>
  tabNotes?: Partial<Record<PaymentTabKey, ReactNode>>
  /** 锁定会员档位；设置后默认打开该页，其他会员页不可切换 */
  lockedTabKey?: PaymentTabKey
  /** 默认选中的推荐档位，不限制用户选择其他满足门槛的档位。 */
  defaultTabKey?: PaymentTabKey
  /** 最低可选档位；未配置 defaultTabKey 时默认打开该页，更低档位不可切换。 */
  minimumTabKey?: PaymentTabKey
  /** 低于最低可选档位时的业务说明；不影响其他来源的默认提示。 */
  minimumTabHint?: ReactNode
  /** 各会员 tab 商品卡片上方一行自定义横幅（per-tab，缺失不渲染） */
  cardBanner?: Partial<Record<PaymentTabKey, ReactNode>>
  /** 可选订单生命周期旁路；没有配置时保持原支付行为。 */
  paymentObserverFactory?: PaymentOrderObserverFactory
}

/** variant 的渲染器 · 入参是 store 里可序列化 data，出参是 JSX 插槽 */
export type PaymentContentRenderer = (data: any) => PaymentContentSlots | null

const registry = new Map<string, PaymentContentRenderer>()

/** 业务功能注册自己的渲染器（模块加载时调用一次） */
export function registerPaymentContent(
  variant: string,
  renderer: PaymentContentRenderer
): void {
  registry.set(variant, renderer)
}

/** 弹窗唯一消费入口：{variant,data} → 插槽；查不到/抛错都回空插槽（→ 兜底） */
export function resolvePaymentContent(
  variant?: string,
  data?: any
): PaymentContentSlots {
  if (!variant) return {}
  const renderer = registry.get(variant)
  if (!renderer) return {}
  try {
    return renderer(data) || {}
  } catch {
    return {}
  }
}
